import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import GraphView from '../GraphView/GraphView'
import Chat from '../chat/Chat'
import FeedFilters from '../feed/FeedFilters'
import useDraggable from '../../../hooks/useDraggable'
import { useFilesContext } from '../../../contexts/FilesContext'
import {
  transformJobs, transformCourses, transformEvents, transformNews,
} from '../../../utils/FeedTransformData'
import styles from './DiscoverView.module.css'

const API_URL = process.env.REACT_APP_API_URL || ''

const EMPTY_FILTERS = {
  domain_keys: [],
  country_codes: [],
  job_types: [],
  required_languages: [],
  publishers: [],
  providers: [],
}

// ── Colour config ──────────────────────────────────────────────────────
const TYPE_META = {
  Jobs:    { color: '#7c4dff', bg: 'rgba(124,77,255,0.10)', action: 'Apply',      endpoint: 'jobs',    transform: transformJobs    },
  Courses: { color: '#00acc1', bg: 'rgba(0,172,193,0.10)',  action: 'Enroll',     endpoint: 'courses', transform: transformCourses },
  Events:  { color: '#2e7d32', bg: 'rgba(46,125,50,0.10)',  action: 'Join',       endpoint: 'events',  transform: transformEvents  },
  News:    { color: '#ef6c00', bg: 'rgba(239,108,0,0.10)',  action: 'Read',       endpoint: 'news',    transform: transformNews    },
}

const arrayOf = (d) => (Array.isArray(d) ? d : d?.data ?? d?.items ?? [])

const timeAgo = (iso) => {
  if (!iso) return ''
  const diff = (Date.now() - new Date(iso).getTime()) / 86400000
  if (diff < 1) return 'Today'
  if (diff < 2) return '1d ago'
  if (diff < 7) return `${Math.floor(diff)}d ago`
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`
  return `${Math.floor(diff / 30)}mo ago`
}

// ── SVG Icons ──────────────────────────────────────────────────────────
const GridIcon  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
const BagIcon   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
const BookIcon  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
const CalIcon   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
const NewsIcon  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8M15 18h-5M10 6h8v4h-8z"/></svg>
const MapIcon   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="4" r="2"/><circle cx="4" cy="20" r="2"/><circle cx="20" cy="20" r="2"/><line x1="12" y1="6" x2="4" y2="18"/><line x1="12" y1="6" x2="20" y2="18"/><line x1="6" y1="20" x2="18" y2="20"/></svg>
const ChatBubble = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
const SearchIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
const CloseIcon  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const ExtIcon    = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
const FilterIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>

const NAV = [
  { id: 'All',     icon: <GridIcon />, label: 'All'     },
  { id: 'Jobs',    icon: <BagIcon />,  label: 'Jobs'    },
  { id: 'Courses', icon: <BookIcon />, label: 'Courses' },
  { id: 'Events',  icon: <CalIcon />,  label: 'Events'  },
  { id: 'News',    icon: <NewsIcon />, label: 'News'    },
]

// ── Skeleton ───────────────────────────────────────────────────────────
const CardSkeleton = () => (
  <div className={styles.skeleton}>
    <div className={styles.skeletonStrip} />
    <div className={styles.skeletonInner}>
      <div className={styles.skeletonHead} />
      <div className={styles.skeletonLine} style={{ width: '80%' }} />
      <div className={styles.skeletonLine} style={{ width: '55%' }} />
      <div className={styles.skeletonBtn} />
    </div>
  </div>
)

// ── Item card ──────────────────────────────────────────────────────────
function ItemCard({ item, hintDelay, onAddToChat }) {
  const navigate  = useNavigate()
  const { onDragStart, onDragEnd } = useDraggable()
  const meta      = TYPE_META[item.category] || TYPE_META.Jobs
  const company   = item.company || item.source || item.location || ''
  const initial   = company?.[0]?.toUpperCase() || item.category[0]
  const isHinting = hintDelay !== undefined

  const handleJobFitCheck = (e) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/dashboard/job-fit-check/${item.id}`)
  }

  return (
    <div
      className={`${styles.card} ${isHinting ? styles.cardWiggle : ''}`}
      style={isHinting ? { animationDelay: `${hintDelay}ms` } : {}}
      draggable
      onDragStart={e => onDragStart(e, item)}
      onDragEnd={onDragEnd}
    >
      <div className={styles.cardStrip} style={{ background: meta.color }} />
      <div className={styles.cardBody}>

        {/* Header row */}
        <div className={styles.cardTop}>
          <span className={styles.cardInitial} style={{ background: meta.bg, color: meta.color }}>
            {initial}
          </span>
          <span className={styles.cardTag} style={{ background: meta.bg, color: meta.color }}>
            {item.category}
          </span>
        </div>

        {/* Content — grows to push footer down */}
        <div className={styles.cardContent}>
          <h3 className={styles.cardTitle}>{item.title}</h3>
          {company   && <p className={styles.cardCompany}>{company}</p>}
          {item.datePosted && <p className={styles.cardDate}>{timeAgo(item.datePosted)}</p>}
        </div>

        {/* Footer — always pinned to bottom */}
        <div className={styles.cardFooter}>
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.cardBtn}
              style={{ background: meta.color }}
            >
              {meta.action} <ExtIcon />
            </a>
          ) : (
            <button className={styles.cardBtn} style={{ background: meta.color }}>
              {meta.action}
            </button>
          )}

          {/* Job Fit Check — only for Jobs */}
          {item.category === 'Jobs' && item.id && (
            <button
              className={styles.cardBtnSecondary}
              style={{ borderColor: meta.color, color: meta.color }}
              onClick={handleJobFitCheck}
            >
              Job Fit
            </button>
          )}

          {/* Add to Chat — mobile only, replaces drag-and-drop */}
          <button
            className={styles.cardAddToChat}
            style={{ borderColor: meta.color, color: meta.color }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddToChat(item) }}
          >
            + Chat
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────
export default function DiscoverView() {
  // Seed the search from ?q= so other pages can deep-link into the feed.
  // Spotlight booths use it for "See all on Chimp", which needs the feed to
  // open already filtered to that company rather than at everything.
  const initialQuery = (() => {
    try {
      return new URLSearchParams(window.location.search).get('q') || ''
    } catch {
      return ''
    }
  })()

  const [activeType, setActiveType]           = useState('All')
  const [search, setSearch]                   = useState(initialQuery)
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery)
  const [advancedFilters, setAdvancedFilters] = useState({ ...EMPTY_FILTERS })
  const [showFilters, setShowFilters]         = useState(false)
  const [showGraph, setShowGraph]             = useState(false)
  const [showChat, setShowChat]               = useState(false)
  const [dragHint, setDragHint]               = useState(false)
  const [items, setItems]                     = useState([])
  const [loading, setLoading]                 = useState(true)
  const [page, setPage]                       = useState(1)
  const [hasMore, setHasMore]                 = useState(true)
  const contentRef = useRef(null)

  // Listen for drawer-dispatched toggle events (Map / Chat buttons in hamburger menu)
  useEffect(() => {
    const onToggleMap  = () => setShowGraph(v => !v)
    const onToggleChat = () => setShowChat(v => !v)
    window.addEventListener('chimp:toggleMap',  onToggleMap)
    window.addEventListener('chimp:toggleChat', onToggleChat)
    return () => {
      window.removeEventListener('chimp:toggleMap',  onToggleMap)
      window.removeEventListener('chimp:toggleChat', onToggleChat)
    }
  }, [])

  // Wiggle cards whenever chat opens — hints that items are draggable
  useEffect(() => {
    if (!showChat) return
    setDragHint(true)
    const t = setTimeout(() => setDragHint(false), 2400)
    return () => clearTimeout(t)
  }, [showChat])

  // Debounce search & reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 380)
    return () => clearTimeout(t)
  }, [search])

  const handleFiltersChange = useCallback((f) => {
    setAdvancedFilters(f)
    setPage(1)
  }, [])

  const handleTypeChange = (type) => {
    setActiveType(type)
    setPage(1)
    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  // ── Fetch ────────────────────────────────────────────────────────────
  const fetchItems = useCallback(async (type, term, af, pg) => {
    setLoading(true)

    const q   = term ? `&search=${encodeURIComponent(term)}` : ''
    const cp  = []
    if (af.domain_keys.length)   cp.push(`domain_keys=${af.domain_keys.join(',')}`)
    if (af.country_codes.length) cp.push(`country_codes=${af.country_codes.join(',')}`)
    const cq  = cp.length ? '&' + cp.join('&') : ''

    const jp = []
    if (af.job_types.length)          jp.push(`job_types=${af.job_types.join(',')}`)
    if (af.required_languages.length) jp.push(`required_languages=${af.required_languages.join(',')}`)
    const jq   = jp.length ? '&' + jp.join('&') : ''
    const curq = af.publishers.length ? `&publishers=${af.publishers.join(',')}` : ''
    const nq   = af.providers.length  ? `&providers=${af.providers.join(',')}` : ''

    const limit = type === 'All' ? 6 : 10

    try {
      let combined = []
      if (type === 'All') {
        const [j, c, e, n] = await Promise.all([
          fetch(`${API_URL}/feed/jobs?page=${pg}&limit=${limit}${q}${cq}${jq}`).then(r => r.ok ? r.json() : []),
          fetch(`${API_URL}/feed/courses?page=${pg}&limit=${limit}${q}${cq}${curq}`).then(r => r.ok ? r.json() : []),
          fetch(`${API_URL}/feed/events?page=${pg}&limit=${limit}${q}${cq}`).then(r => r.ok ? r.json() : []),
          fetch(`${API_URL}/feed/news?page=${pg}&limit=${limit}${q}${cq}${nq}`).then(r => r.ok ? r.json() : []),
        ])
        combined = [
          ...transformJobs(arrayOf(j)),
          ...transformCourses(arrayOf(c)),
          ...transformEvents(arrayOf(e)),
          ...transformNews(arrayOf(n)),
        ].sort((a, b) => new Date(b.datePosted || 0) - new Date(a.datePosted || 0))
        setHasMore(combined.length > 0)
      } else {
        const { endpoint, transform } = TYPE_META[type]
        const xq = type === 'Jobs' ? jq : type === 'Courses' ? curq : type === 'News' ? nq : ''
        const r  = await fetch(`${API_URL}/feed/${endpoint}?page=${pg}&limit=${limit}${q}${cq}${xq}`)
        const d  = r.ok ? await r.json() : []
        combined = transform(arrayOf(d))
        setHasMore(combined.length === limit)
      }
      setItems(combined)
    } catch (err) {
      console.error('DiscoverView fetch:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchItems(activeType, debouncedSearch, advancedFilters, page)
  }, [activeType, debouncedSearch, advancedFilters, page, fetchItems])

  const handlePrev = () => {
    if (page > 1) {
      setPage(p => p - 1)
      if (contentRef.current) contentRef.current.scrollTop = 0
    }
  }

  const handleNext = () => {
    if (hasMore && items.length > 0) {
      setPage(p => p + 1)
      if (contentRef.current) contentRef.current.scrollTop = 0
    }
  }

  const hasActiveFilters = Object.values(advancedFilters).some(v => v.length > 0)

  // ── File drop into chat panel ────────────────────────────────────────
  const { addFile } = useFilesContext()
  const [isDraggingOverChat, setIsDraggingOverChat] = useState(false)

  const handleChatDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOverChat(false)
    try {
      const raw = e.dataTransfer.getData('application/json')
      if (raw) addFile(JSON.parse(raw))
    } catch {}
  }

  const handleChatDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    if (!isDraggingOverChat) setIsDraggingOverChat(true)
  }

  const handleChatDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingOverChat(false)
    }
  }

  // Chat styles — forces Chat to fill the panel correctly on all screen sizes.
  // The inline overrides neutralise Chat.module.css's mobile rules that set
  // display:none, position:fixed, bottom:60px, width:100vw (legacy standalone layout).
  const chatStyles = {
    display: 'flex',
    position: 'static',
    width: '100%',
    height: '100%',
    bottom: 'auto',
    left: 'auto',
    maxWidth: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    background: 'transparent',
    padding: '12px 14px',
  }

  return (
    <div className={styles.root}>

      {/* ── Left icon nav ─────────────────────────────────── */}
      <nav className={styles.iconNav}>
        <div className={styles.navTop}>
          {NAV.map(n => (
            <button
              key={n.id}
              className={`${styles.navBtn} ${activeType === n.id ? styles.navBtnActive : ''}`}
              onClick={() => handleTypeChange(n.id)}
              title={n.label}
            >
              {n.icon}
              <span className={styles.navLabel}>{n.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.navBottom}>
          <button
            className={`${styles.navBtn} ${showGraph ? styles.navBtnActive : ''}`}
            onClick={() => setShowGraph(v => !v)}
            title="Network Map"
          >
            <MapIcon />
            <span className={styles.navLabel}>Map</span>
          </button>
          <button
            className={`${styles.navBtn} ${showChat ? styles.navBtnActive : ''}`}
            onClick={() => setShowChat(v => !v)}
            title="Chat"
          >
            <ChatBubble />
            <span className={styles.navLabel}>Chat</span>
          </button>
        </div>
      </nav>

      {/* ── Filter backdrop (tablet/mobile: tap outside to close) ── */}
      {showFilters && (
        <div className={styles.filterBackdrop} onClick={() => setShowFilters(false)} />
      )}

      {/* ── Collapsible filter sidebar ────────────────────── */}
      <div className={`${styles.filterSidebar} ${showFilters ? styles.filterSidebarOpen : ''}`}>
        <FeedFilters
          activeCategory={activeType}
          filters={advancedFilters}
          onFiltersChange={handleFiltersChange}
        />
      </div>

      {/* ── Main content ──────────────────────────────────── */}
      <div className={styles.main}>

        {/* Top bar: filter toggle + search + count */}
        <div className={styles.topBar}>
          <button
            className={`${styles.filterToggle} ${showFilters ? styles.filterToggleActive : ''}`}
            onClick={() => setShowFilters(v => !v)}
            title="Toggle Filters"
          >
            <FilterIcon />
            <span>Filters</span>
            {hasActiveFilters && <span className={styles.filterDot} />}
          </button>

          <div className={styles.searchWrap}>
            <span className={styles.searchIconWrap}><SearchIcon /></span>
            <input
              className={styles.searchInput}
              placeholder={`Search ${activeType === 'All' ? 'everything' : activeType.toLowerCase()}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.searchClear} onClick={() => setSearch('')}>
                <CloseIcon />
              </button>
            )}
          </div>

          {!loading && (
            <span className={styles.resultCount}>
              {items.length}{hasMore ? '+' : ''} {activeType === 'All' ? 'results' : activeType.toLowerCase()}
            </span>
          )}
        </div>

        {/* Mobile-only horizontal category chips (iconNav is hidden on mobile) */}
        <div className={styles.mobileCategoryRow}>
          {NAV.map(n => (
            <button
              key={n.id}
              className={`${styles.mobileCategoryChip} ${activeType === n.id ? styles.mobileCategoryChipActive : ''}`}
              onClick={() => handleTypeChange(n.id)}
            >
              {n.label}
            </button>
          ))}
        </div>

        {/* Scrollable card area */}
        <div className={styles.contentArea} ref={contentRef}>
          {loading ? (
            <div className={styles.grid}>
              {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
            </div>
          ) : items.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}>🔍</span>
              <p>No results found</p>
              <span>Try adjusting your filters or search term</span>
            </div>
          ) : (
            <div className={styles.grid}>
              {items.map((item, idx) => (
                <ItemCard
                  key={`${item.category}-${item.id}`}
                  item={item}
                  hintDelay={dragHint ? idx * 55 : undefined}
                  onAddToChat={(it) => { addFile(it); setShowChat(true) }}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && items.length > 0 && (
            <div className={styles.pagination}>
              <button className={styles.pageBtn} onClick={handlePrev} disabled={page === 1}>
                Previous
              </button>
              <span className={styles.pageNum}>Page {page}</span>
              <button className={styles.pageBtn} onClick={handleNext} disabled={!hasMore || items.length === 0}>
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Chat side panel (slides in from right) ────────── */}
      <div className={`${styles.chatPanel} ${showChat ? styles.chatPanelOpen : ''}`}>
        <div className={styles.chatPanelHeader}>
          <div>
            <span className={styles.chatPanelTitle}>Chimp</span>
            <p className={styles.chatPanelHint}>Drag any card here to discuss it</p>
          </div>
          <button className={styles.chatPanelClose} onClick={() => setShowChat(false)}>
            <CloseIcon />
          </button>
        </div>
        <div
          className={styles.chatPanelBody}
          onDrop={handleChatDrop}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
        >
          <Chat styles={chatStyles} />
          {isDraggingOverChat && (
            <div
              className={styles.chatDropOverlay}
              onDrop={handleChatDrop}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
            >
              <span className={styles.chatDropIcon}>📎</span>
              <p className={styles.chatDropLabel}>Drop to attach</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Graph overlay (full screen) ───────────────────── */}
      {showGraph && (
        <div className={styles.graphOverlay}>
          <GraphView />
          <button className={styles.graphClose} onClick={() => setShowGraph(false)}>
            <CloseIcon /> Close Map
          </button>
        </div>
      )}

    </div>
  )
}
